import { useEffect, useState, useCallback, useRef } from "react";
import {
  useSearchStore,
  initSearchPusher,
  SearchResult,
} from "@/store/search-store";
import { useRouter } from "next/navigation";
import { useDebounce } from "@/hooks/use-debounce";

type SearchAction = (query: string) => Promise<{
  success?: boolean;
  results?: SearchResult[];
}>;

export function useSearch(
  customSearchAction?: SearchAction,
  companyId?: string
) {
  const router = useRouter();
  const {
    query,
    results,
    isLoading,
    error,
    recentSearches,
    setQuery,
    search: defaultSearch,
    clearSearch,
    clearResults,
    clearRecentSearches,
  } = useSearchStore();

  const [isOpen, setIsOpen] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const searchResultsRef = useRef<HTMLDivElement>(null);
  const lastSearchedQueryRef = useRef<string>("");

  // Store companyId in a ref to avoid dependency changes
  const companyIdRef = useRef<string | undefined>(companyId);

  // Update companyIdRef if companyId changes
  useEffect(() => {
    companyIdRef.current = companyId;
  }, [companyId]);

  // Debounce the search query for better performance
  const debouncedQuery = useDebounce(query, 300);

  // Initialize pusher subscription
  useEffect(() => {
    const cleanup = initSearchPusher();
    return () => {
      if (cleanup) cleanup();
    };
  }, []);

  // Load recent searches from localStorage on mount
  useEffect(() => {
    if (typeof window !== "undefined") {
      try {
        const storedSearches = localStorage.getItem("recentSearches");
        if (storedSearches) {
          const parsedSearches = JSON.parse(storedSearches);
          if (Array.isArray(parsedSearches)) {
            useSearchStore.setState({ recentSearches: parsedSearches });
          }
        }
      } catch (error) {
        console.error("Error loading recent searches:", error);
      }
    }
  }, []);

  // Use custom search action if provided, otherwise use the default from store
  const search = useCallback(
    async (searchQuery: string) => {
      // Skip if query is the same as the last one we searched for
      if (searchQuery === lastSearchedQueryRef.current) {
        return;
      }

      lastSearchedQueryRef.current = searchQuery;

      if (customSearchAction) {
        const normalizedQuery = searchQuery.trim();

        if (!normalizedQuery) {
          useSearchStore.setState({ results: [], query: "" });
          return;
        }

        useSearchStore.setState({
          isLoading: true,
          query: normalizedQuery,
          error: null,
        });

        try {

          const response = await customSearchAction(normalizedQuery);

          // Accept any response with a results array, even if empty
          if (response && response.success && Array.isArray(response.results)) {
            useSearchStore.setState({
              results: response.results,
              isLoading: false,
            });

            // Add to recent searches only if we got results
            if (response.results.length > 0) {
              const currentSearches = useSearchStore.getState().recentSearches;
              // Remove if exists already (to reorder)
              const filteredSearches = currentSearches.filter(
                (s) => s !== normalizedQuery
              );
              // Add to beginning and limit length
              const newSearches = [normalizedQuery, ...filteredSearches].slice(
                0,
                5
              );
              useSearchStore.setState({ recentSearches: newSearches });

              // Persist to localStorage
              if (typeof window !== "undefined") {
                localStorage.setItem(
                  "recentSearches",
                  JSON.stringify(newSearches)
                );
              }
            }
          }
        } catch (error) {
          console.error("Error searching products:", error);
          useSearchStore.setState({
            results: [],
            isLoading: false,
            error: "An error occurred while searching. Please try again.",
          });
        }
      } else {
        // Use default search from store
        defaultSearch(searchQuery);
      }
    },
    [customSearchAction, defaultSearch]
  );

  // Perform search when debounced query changes
  useEffect(() => {
    if (debouncedQuery) {
      search(debouncedQuery);
    } else {
      clearResults();
      lastSearchedQueryRef.current = "";
    }
  }, [debouncedQuery, search, clearResults]);

  // Handle click outside to close search results
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        searchResultsRef.current &&
        searchInputRef.current &&
        !searchResultsRef.current.contains(event.target as Node) &&
        !searchInputRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Handle escape key to close search
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    };

    document.addEventListener("keydown", handleEscKey);
    return () => {
      document.removeEventListener("keydown", handleEscKey);
    };
  }, []);

  const handleSearch = useCallback(
    (searchQuery: string) => {
      setQuery(searchQuery);
      setIsOpen(true);
    },
    [setQuery]
  );

  const handleItemClick = useCallback(
    (url: string) => {
      clearSearch();
      setIsOpen(false);
      router.push(url);
    },
    [clearSearch, router]
  );

  const handleRecentSearchClick = useCallback(
    (searchQuery: string) => {
      setQuery(searchQuery);
      // Reset the last searched query to force a new search
      lastSearchedQueryRef.current = "";
      search(searchQuery);
    },
    [setQuery, search]
  );

  const openSearch = useCallback(() => {
    setIsOpen(true);
    setTimeout(() => {
      searchInputRef.current?.focus();
    }, 10);
  }, []);

  const closeSearch = useCallback(() => {
    setIsOpen(false);
    clearSearch();
    lastSearchedQueryRef.current = "";
  }, [clearSearch]);

  return {
    query,
    results,
    isLoading,
    error,
    recentSearches,
    isOpen,
    searchInputRef,
    searchResultsRef,
    handleSearch,
    handleItemClick,
    handleRecentSearchClick,
    clearRecentSearches,
    openSearch,
    closeSearch,
    setIsOpen,
  };
}
